import React, { useEffect, useState, useRef } from "react";

import useFetch from "../../hooks/useFetch";
import { applyDiscountRules, capitalizeWords } from "../../helpers/common";
import { useFilters } from "../../hooks/useFilters";

import { useSelector } from "react-redux";

import { useParams, useLocation } from "react-router-dom";
import { ReactComponent as Close } from "../../SVGs/Close.svg";

import useDeviceType from "../../hooks/useDeviceType";

import "./Category.scss";

import Card from "../../components/Card/Card";
import LoaderOverlay from "../../components/LoaderOverlay/LoaderOverlay";
import CircularProgress from "@mui/material/CircularProgress";

import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
 
const Category = ({ catType }) => {
  const slug = useParams().slug;
  const pageType = catType;

  const deviceType = useDeviceType();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const currentLanguage = i18n.language;
  const loadMoreRef = useRef(null);

  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");

  const discountRules = useSelector((state) => state.discountRules);

  // Filters 
  const {
    filters,
    areFiltersLoading,
    areFiltersError,
    setAreFiltersError,
    retryFilters,
  } = useFilters();
  const [selectedFilters, setSelectedFilters] = useState(new Map());
  const [expandedFilters, setExpandedFilters] = useState({});

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  // Product States
  const [productsList, setProductsList] = useState([]);
  const [productsMetaData, setProductsMetaData] = useState(null);

  const [isProductsLoading, setIsProductsLoading] = useState(true);
  const [isProductsError, setIsProductsError] = useState(false);
  const [productsRetries, setProductsRetries] = useState(0);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);

  const [isNewPageLoading, setIsNewPageLoading] = useState(false);

  const toggleFiltersVisibility = () => {
    setIsFiltersVisible((prevState) => !prevState);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  let productsBaseUrl = "/products";
  let productsParams = new URLSearchParams({
      populate: "deep,4",
    "pagination[page]": page,
    "pagination[pageSize]": pageSize,
    "filters[Status][$eq]": true,
    sort: "CreationDate:desc",
    locale: "en",
  });
  if (productsRetries > 0) productsParams.set("_retry", productsRetries);

  if (slug == "best-sellers") {
    productsBaseUrl = "/best-sellers";
    productsParams = new URLSearchParams({
      populate: "deep,4",
      "pagination[page]": page,
      "pagination[pageSize]": pageSize,
      "filters[Product][Status][$eq]": true,
      sort: "SortOrder",
      locale: "en",
    });
    if (productsRetries > 0) productsParams.set("_retry", productsRetries);
  }

  let filterKey = "";
  let secondFilterKey = "";
  if (pageType == "category") {
    filterKey = "filters[Categories][Title][$eq]";
  } else if (pageType == "tag") {
    filterKey = "filters[Tags][Name][$eq]";
  } else if (pageType == "search") {
    filterKey = "filters[$or][0][Title][$contains]";
    secondFilterKey = "filters[$or][1][localizations][Title][$contains]";
  }

  if (slug == "new") {
    productsParams.set("sort", "CreationDate:desc");
  } else if (slug != "best-sellers") {
    productsParams.set(filterKey, slug);
    if (pageType == "search") {
      productsParams.set(secondFilterKey, slug);
    }
  }

  selectedFilters.forEach((ids, filter) => {
    let index = 0;
    switch (filter) {
      case "Vendor":
        ids.forEach((id) => {
          if (slug == "best-sellers") productsParams.append(`filters[Product][vendor][id][$in][${index}]`, id);
          else productsParams.append(`filters[vendor][id][$in][${index}]`, id);
          index++;
        });
        break;
      case "Color":
        ids.forEach((id) => {
          if (slug == "best-sellers") productsParams.append(`filters[Product][Variants][Color][id][$in][${index}]`, id);
          else productsParams.append(`filters[Variants][Color][id][$in][${index}]`, id);
          index++;
        });
        break;
      case "Size":
        ids.forEach((id) => {
          if (slug == "best-sellers") productsParams.append(`filters[Product][Variants][Sizes][Size][id][$in][${index}]`, id);
          else productsParams.append(`filters[Variants][Sizes][Size][id][$in][${index}]`, id);
          index++;
        });
        break;
      case "Price":
        ids.forEach((id) => {
          if (slug == "best-sellers") productsParams.append(`filters[Product][Price][$lt]`, id);
          else productsParams.append(`filters[Price][$lt]`, id);
          index++;
        });
        break;
      default:
        break;
    }
  });

  const productsUrl = `${productsBaseUrl}?${productsParams.toString()}`;
  const {
    data: productsData,
    loading: productsLoading,
    error: productsError,
    meta: productsMeta,
  } = useFetch(productsUrl, true);

  // Category
  const [categoryInfo, setCategoryInfo] = useState({});
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [isCategoryError, setIsCategoryError] = useState(false);
  const [categoryRetries, setCategoryRetries] = useState(0);
  const [titleParam, setTitleParam] = useState("");

  const categoryBaseUrl = "/categories";
  const categoryParams = new URLSearchParams({
    populate: "*",
    locale: "en",
    "filters[Title][$eq]": slug,
  });
  if (categoryRetries > 0) categoryParams.set("_retry", categoryRetries);
  const categoryUrl = `${categoryBaseUrl}?${categoryParams.toString()}`;
  const {
    data: categoryData,
    loading: categoryLoading,
    error: categoryError,
  } = useFetch(categoryUrl);

  const retryProducts = () => setProductsRetries((prev) => prev + 1);
  const retryCategory = () => setCategoryRetries((prev) => prev + 1);

  const toggleShowAll = (index) => {
    setExpandedFilters((prev) => {
      return {
        ...prev,
        [index]: !prev[index],
      };
    });
  };

  const handleFilterChange = (event) => {
    const [id, filter] = event.target.id.split("-");
    const isChecked = event.target.checked;

    setSelectedFilters((prev) => {
      const newFilters = new Map(prev);

      if (isChecked) {
        if (!newFilters.has(filter)) {
          newFilters.set(filter, new Set());
        }
        newFilters.get(filter).add(id);
      } else {
        if (newFilters.has(filter)) {
          newFilters.get(filter).delete(id);
          if (newFilters.get(filter).size === 0) {
            newFilters.delete(filter);
          }
        }
      }
      return newFilters;
    });

    setProductsList([]);
    setPage(1);
    setIsProductsLoading(true); 
  };

  const getCategoryName = (cat) => {
    let catName = cat?.attributes?.Title;
    if (currentLanguage != "en") {
      const localization = cat?.attributes?.localizations?.data?.find(
        (local) => local?.attributes?.locale === currentLanguage
      );
      if (localization) {
        catName = localization?.attributes?.Title;
      }
    }
    return catName;
  };

  const getName = (color) => {
    let colorName = color?.attributes?.Name;
    if (currentLanguage != "en") {
      const localization = color?.attributes?.localizations?.data?.find(
        (local) => local?.attributes?.locale === currentLanguage
      );
      if (localization) {
        colorName = localization?.attributes?.Name;
      }
    }
    return colorName;
  };

  useEffect(() => {
    if (!categoryLoading && categoryData) {
      setCategoryInfo(categoryData[0]);
      setIsCategoryLoading(false);
      if (pageType == "category" && slug != "new" && slug != "best-sellers") {
        setMetaTitle(categoryData[0]?.attributes?.SEO?.metaTitle);
        setMetaDescription(categoryData[0]?.attributes?.SEO?.metaDescription);
        setMetaKeywords(categoryData[0]?.attributes?.SEO?.keywords);
      }
    } else if (categoryLoading && !categoryError) {
      setIsCategoryLoading(true);
    } else if (!categoryLoading && categoryError && !categoryData) {
      setIsCategoryLoading(false);
      setIsCategoryError(true);
    }
  }, [categoryLoading, categoryData, categoryError, pageType, slug]);

  useEffect(() => {
    setProductsList([]);
    setPage(1);
    setIsProductsLoading(true); 
  }, [slug, pageType]);

  useEffect(() => {
    if (!productsLoading && productsData) {
      let prods = [];
      if (slug == "best-sellers") {
        prods = productsData?.map((prod) => prod?.attributes?.Product?.data) || [];
      } else {
        prods = [...productsData];
      }

      const discountedProductsData = applyDiscountRules(prods, discountRules);

      setProductsList((prevProducts) => {
        if (page === 1) {
          return [...discountedProductsData];
        } else {
          const existingIds = new Set(prevProducts.map((p) => p?.id));
          const uniqueNewProds = discountedProductsData.filter((p) => p && !existingIds.has(p.id));
          return [...prevProducts, ...uniqueNewProds];
        }
      });

      setProductsMetaData(productsMeta);
      setIsProductsLoading(false);
      setIsNewPageLoading(false);
    } else if (productsLoading && !productsError) {
      if (page === 1) {
        setIsProductsLoading(true);
      } else {
        setIsNewPageLoading(true);
      }
    } else if (!productsLoading && productsError && !productsData) {
      setIsProductsLoading(false);
      setIsNewPageLoading(false);
      setIsProductsError(true);
    }
  }, [productsLoading, productsData, productsError, page, discountRules, productsMeta, slug]);

  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !productsLoading &&
          !isNewPageLoading &&
          productsMetaData &&
          page < productsMetaData?.pagination?.pageCount
        ) {
          setPage((prevPage) => prevPage + 1);
          setIsNewPageLoading(true);
        }
      },
      { threshold: 0.25 }
    );

    observer.observe(loadMoreRef.current);

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [productsMetaData, productsLoading, page, isNewPageLoading]);

  useEffect(() => {
    if (slug != "new" && slug != "best-sellers") {
      if (pageType == "category") {
        document.title = `${t("general.classy-ym")} - ${t("category.category")} ${capitalizeWords(slug)}`;
      } else if (pageType == "tag") {
        document.title = `${t("general.classy-ym")} - ${t("category.tag")} ${capitalizeWords(slug)}`;
      } else if (pageType == "search") {
        document.title = `${t("general.classy-ym")} - ${t("category.search-for")} ${capitalizeWords(slug)}`;
      }
    } else {
      if (slug == "new") {
        document.title = `${t("general.classy-ym")} - ${t("home.new-in")}`;
      } else if (slug == "best-sellers") {
        document.title = `${t("general.classy-ym")} - ${t("home.best-sellers")}`;
      }
    }

    const searchParams = new URLSearchParams(location.search);
    const titleSearchParam = searchParams.get("title");
    if (currentLanguage != "en" && pageType != "search") setTitleParam(titleSearchParam);
    else setTitleParam(slug);
  }, [slug, pageType, t, location.search, currentLanguage]);

  return (
    <>
      <Helmet>
        <meta name="description" content={metaDescription || t("home.default-description")} />
        <meta name="keywords" content={metaKeywords || t("home.default-keywords")} />
      </Helmet>
      <section className="category-page">
        <LoaderOverlay
          loadingState={isCategoryLoading}
          errorState={isCategoryError}
          setErrorState={setIsCategoryError}
          retryFunc={retryCategory}
        />
        {pageType == "category" &&
          slug != "new" &&
          slug != "best-sellers" &&
          categoryInfo?.attributes?.Image?.data?.attributes?.url && (
            <div
              className="banner"
              style={{
                background: `url(${
                  process.env.REACT_APP_IMAGE_URL + categoryInfo?.attributes?.Image?.data?.attributes?.url
                }) no-repeat center center / cover`,
              }}
            ></div>
          )}
        <div className="category-content">
          <h1 className="category-title">
            {slug == "new" || slug == "best-sellers" ? (
              <>
                {slug == "new" && t("category.new")}
                {slug == "best-sellers" && t("home.best-sellers")}
              </>
            ) : (
              <>
                {pageType == "search" && t("category.search-results-for")}{" "}
                {pageType == "category" ? getCategoryName(categoryInfo) : titleParam}
              </>
            )}
          </h1>
          <button className="filters-toggle-btn" onClick={toggleFiltersVisibility}>
            <p>{t("category.filters")}</p> <img src="/img/filter.svg" alt="filters" />
          </button>

          <div className="container">
            <div className={`filters ${deviceType == "mobile" ? (isFiltersVisible ? "visible" : "hidden") : ""}`}>
              {areFiltersLoading && !isProductsLoading && !isCategoryLoading ? (
                <LoaderOverlay
                  loadingState={areFiltersLoading}
                  errorState={areFiltersError}
                  setErrorState={setAreFiltersError}
                  retryFunc={retryFilters}
                />
              ) : (
                <></>
              )}
              {filters.map((filter, index) => {
                const isExpanded = expandedFilters[index] || false;

                return (
                  <div className="filter-group" key={index}>
                    <h2 className="title">{filter.title}</h2>
                    <div className="values">
                      {(isExpanded ? filter.values : filter.values.slice(0, 5)).map((value) => {
                        return (
                          <div className="filter" key={value.id}>
                            <input
                              className="filter-checkbox"
                              type="checkbox"
                              name={value?.attributes?.Name}
                              id={`${value.id}-${filter.title}`}
                              onChange={handleFilterChange}
                              checked={selectedFilters.get(filter.title)?.has(String(value.id)) || false}
                            />
                            <label className="filter-title" htmlFor={`${value.id}-${filter.title}`}>
                              {getName(value)}
                            </label>
                          </div>
                        );
                      })}
                      {filter.values.length > 5 ? (
                        <div className="show-more-filters" onClick={() => toggleShowAll(index)}>
                          {isExpanded ? t("category.show-less") : `${t("category.show-all")} (${filter.values.length})`}
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                );
              })}
              <Close className="search-btn" onClick={toggleFiltersVisibility} />
            </div>
            <div className="products">
              {isProductsLoading && !areFiltersLoading && !isCategoryLoading && !isNewPageLoading ? (
                <LoaderOverlay
                  loadingState={isProductsLoading}
                  errorState={isProductsError}
                  setErrorState={setIsProductsError}
                  retryFunc={retryProducts}
                />
              ) : (
                <></>
              )}
              <div className="products-container">
               
                {productsList.length === 0 && !productsLoading && !isProductsLoading ? (
                  <span className="empty-list">{t("home.no-products-available")}</span>
                ) : (
                  <></>
                )}

                {productsList.map((product, idx) => {
                  if (!product) return null;
                  return (
                    <Card
                      key={`${product.id}-${idx}`}
                      areDiscountRulesApplied={true}
                      product={product}
                      isCategory={true}
                    />
                  );
                })} 
              </div>
              {productsMetaData?.pagination?.page < productsMetaData?.pagination?.pageCount && pageType !== "search" ? (
                <div className="show-more-products" ref={loadMoreRef}>
                  {isNewPageLoading && <CircularProgress />}
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Category;